// 히어로 localStorage 저장·복원·버전폴백 + ?h= 공유 인코딩.
// FR-HERO-03/04/08/13/15. 모두 동일 출처 localStorage(S1-3) — 서버 불필요(M1).
import {
  HERO_STORAGE_KEY,
  HERO_SCHEMA_VERSION,
  sanitizeHero,
  defaultHero,
  type SavedHero,
} from "@greg-chick/shared";

/** localStorage 에서 히어로 로드 + 검증/폴백(FR-HERO-04/08/15). 없으면 null. */
export function loadHero(): SavedHero | null {
  try {
    const raw = localStorage.getItem(HERO_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    // 손상·구버전이어도 sanitizeHero 가 항상 안전한 값으로 정규화.
    return sanitizeHero(parsed);
  } catch {
    return null; // 파싱 불가 → 신규로 취급
  }
}

/** 히어로 저장(FR-HERO-03). updatedAt 갱신. 서버 미전송. */
export function saveHero(hero: SavedHero): SavedHero {
  const toSave: SavedHero = {
    ...sanitizeHero(hero),
    version: HERO_SCHEMA_VERSION,
    updatedAt: new Date().toISOString(),
  };
  lastSaveOk = true;
  try {
    localStorage.setItem(HERO_STORAGE_KEY, JSON.stringify(toSave));
  } catch {
    /**
     * 🔴 **못 남겼으면 알려 준다.** 예전 주석은 「FR-HERO-07 고지로 안내」였는데
     * **그런 고지가 화면에 없었다.** 사생활 보호 모드에서는 아이가 캐릭터를 다 만들고
     * 「저장됐다」는 화면을 본 뒤, 새로고침 한 번에 잃는다.
     * (그림책 저장에서 똑같은 것을 고쳤다 — 적어 두는 것으로는 알려 주게 되지 않는다.)
     */
    lastSaveOk = false;
  }
  return toSave;
}

let lastSaveOk = true;
/** 마지막 저장이 이 기기에 남았나. 안 남았으면 화면이 말해야 한다. */
export function heroSavedOk(): boolean {
  return lastSaveOk;
}

export function clearHero(): void {
  try {
    localStorage.removeItem(HERO_STORAGE_KEY);
  } catch {
    /* noop */
  }
}

// ── ?h= URL 공유(M1 폴백, 서버 없이 기기 간 최소 복원) — FR-HERO-13 ──
// base64url(JSON) — 비민감(코스튬)이라 암호화 불필요. 로드 시 sanitizeHero 로 방어.

function toBase64Url(s: string): string {
  const b64 = btoa(unescape(encodeURIComponent(s)));
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(s: string): string {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  return decodeURIComponent(escape(atob(b64)));
}

/** 공유용 최소 필드만 인코딩(updatedAt·code 제외 — 기기 로컬 메타). */
export function encodeHeroParam(hero: SavedHero): string {
  const minimal = {
    v: hero.version,
    b: hero.base,
    p: hero.parts,
    c: hero.palette,
    n: hero.nickname,
  };
  return toBase64Url(JSON.stringify(minimal));
}

export function decodeHeroParam(param: string): SavedHero | null {
  try {
    const obj = JSON.parse(fromBase64Url(param)) as Record<string, unknown>;
    return sanitizeHero({
      version: obj.v,
      base: obj.b,
      parts: obj.p,
      palette: obj.c,
      nickname: obj.n,
      updatedAt: new Date().toISOString(),
    });
  } catch {
    return null;
  }
}

/** 현재 URL 의 ?h= 로 들어온 히어로(있으면). FR-HERO-13 기기 간 복원 진입점. */
export function heroFromCurrentUrl(): SavedHero | null {
  try {
    const h = new URLSearchParams(window.location.search).get("h");
    return h ? decodeHeroParam(h) : null;
  } catch {
    return null;
  }
}

export { defaultHero };
